<div class="push-notification-container">
  <div class="header">
    <h2>{{ title }}</h2>
    <button 
      mat-icon-button 
      (click)="refreshStatus()" 
      [disabled]="isLoading"
      matTooltip="Refresh Status">
      <mat-icon>refresh</mat-icon>
    </button>
  </div>

  <!-- Status Display -->
  <div class="status-section">
    <mat-card class="status-card">
      <mat-card-content>
        <div class="status-row">
          <span class="status-label">Status:</span>
          <mat-chip [color]="getStatusColor()" selected>
            {{ getStatusText() }}
          </mat-chip>
        </div>
        
        <div class="status-row">
          <span class="status-label">Permission:</span>
          <mat-chip>{{ subscriptionStatus.permission }}</mat-chip>
        </div>

        <div class="status-row">
          <span class="status-label">Service Worker:</span>
          <mat-chip [color]="isServiceWorkerReady ? 'primary' : 'warn'" selected>
            {{ isServiceWorkerReady ? 'Ready' : 'Not Ready' }}
          </mat-chip>
        </div>

        <div class="status-row">
          <span class="status-label">VAPID Key:</span>
          <mat-chip [color]="hasValidVapidKey ? 'primary' : 'warn'" selected>
            {{ hasValidVapidKey ? 'Configured' : 'Not Configured' }}
          </mat-chip>
        </div>
        
        <div class="status-row" *ngIf="subscriptionStatus.subscription">
          <span class="status-label">Endpoint:</span>
          <code class="endpoint">{{ formatEndpoint(subscriptionStatus.subscription.endpoint) }}</code>
        </div>

        <div class="status-row" *ngIf="subscriptionStatus.error" class="error-row">
          <span class="status-label">Error:</span>
          <span class="error-text">{{ subscriptionStatus.error }}</span>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Main Actions -->
  <div class="main-actions">
    <button 
      mat-raised-button 
      color="primary" 
      (click)="subscribe()" 
      *ngIf="canSubscribe"
      [disabled]="isLoading">
      <mat-icon>notifications_active</mat-icon>
      Subscribe to Notifications
    </button>

    <button 
      mat-raised-button 
      color="warn" 
      (click)="unsubscribe()" 
      *ngIf="canUnsubscribe"
      [disabled]="isLoading">
      <mat-icon>notifications_off</mat-icon>
      Unsubscribe
    </button>
  </div>
  <!-- Send Test Notification Section -->
  <div class="send-notification-section" *ngIf="subscriptionStatus.isSubscribed">
    <mat-card>
      <mat-card-header>
        <mat-card-title>Send Test Notification</mat-card-title>
        <mat-card-subtitle>Test the push notification system with a custom message</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Test Message</mat-label>
          <input 
            matInput 
            [(ngModel)]="testMessage" 
            placeholder="Enter your test message"
            (keyup.enter)="sendTestNotification()"
            maxlength="100">
          <mat-hint>{{ testMessage.length }}/100</mat-hint>
        </mat-form-field>
        
        <div class="send-button-container">
          <button 
            mat-raised-button 
            color="accent" 
            (click)="sendTestNotification()"
            [disabled]="!canSendNotification || isLoading">
            <mat-icon>send</mat-icon>
            Send Test Notification
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
  <!-- Developer Tools -->
  <div class="testing-section" *ngIf="showTestingTools">
    <mat-card>
      <mat-card-header>
        <mat-card-title>Developer Tools</mat-card-title>
        <mat-card-subtitle>Debug and test push notification functionality</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <div class="testing-buttons">
          <button 
            mat-stroked-button 
            color="primary"
            (click)="testServiceWorkerNotification()">
            <mat-icon>build</mat-icon>
            Test Service Worker
          </button>
          
          <button 
            mat-stroked-button 
            (click)="testManualNotification()">
            <mat-icon>notification_important</mat-icon>
            Test Manual Notification
          </button>
          
          <button 
            mat-stroked-button 
            color="warn"
            (click)="forceServiceWorkerUpdate()">
            <mat-icon>update</mat-icon>
            Force SW Update
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
  <!-- Debug Information Panel -->
  <div class="debug-section" *ngIf="showDebugInfo">
    <mat-expansion-panel [expanded]="!!diagnosticInfo">
      <mat-expansion-panel-header>
        <mat-panel-title>
          <mat-icon>bug_report</mat-icon>
          Debug Information
        </mat-panel-title>
        <mat-panel-description>
          Technical details for debugging
          <button 
            mat-icon-button 
            (click)="forceDiagnosticRefresh(); $event.stopPropagation()" 
            [disabled]="isLoading"
            matTooltip="Refresh Debug Info">
            <mat-icon>refresh</mat-icon>
          </button>
        </mat-panel-description>
      </mat-expansion-panel-header>
      
      <div class="debug-content" *ngIf="diagnosticInfo; else debugError">
        <mat-divider></mat-divider>
        <h4>Browser Capabilities</h4>
        <div class="debug-row">
          <span class="debug-label">Service Worker Support:</span>
          <mat-chip [color]="diagnosticInfo.browserCapabilities?.serviceWorkerSupported ? 'primary' : 'warn'" selected>
            {{ diagnosticInfo.browserCapabilities?.serviceWorkerSupported ? 'Yes' : 'No' }}
          </mat-chip>
        </div>
        <div class="debug-row">
          <span class="debug-label">Push Manager Support:</span>
          <mat-chip [color]="diagnosticInfo.browserCapabilities?.pushManagerSupported ? 'primary' : 'warn'" selected>
            {{ diagnosticInfo.browserCapabilities?.pushManagerSupported ? 'Yes' : 'No' }}
          </mat-chip>
        </div>
        <div class="debug-row">
          <span class="debug-label">Notification Support:</span>
          <mat-chip [color]="diagnosticInfo.browserCapabilities?.notificationSupported ? 'primary' : 'warn'" selected>
            {{ diagnosticInfo.browserCapabilities?.notificationSupported ? 'Yes' : 'No' }}
          </mat-chip>
        </div>

        <mat-divider></mat-divider>
        <h4>Configuration</h4>
        <div class="debug-row">
          <span class="debug-label">VAPID Key:</span>
          <code>{{ formatJsonProperty(diagnosticInfo.configuration, 'vapidPublicKey') }}</code>
        </div>
        <div class="debug-row">
          <span class="debug-label">Backend URL:</span>
          <code>{{ formatJsonProperty(diagnosticInfo.configuration, 'backendUrl') }}</code>
        </div>

        <mat-divider></mat-divider>
        <h4>Service Worker</h4>
        <div class="debug-row">
          <span class="debug-label">Registration State:</span>
          <mat-chip>{{ diagnosticInfo.serviceWorker?.registrationState || 'Unknown' }}</mat-chip>
        </div>
        <div class="debug-row" *ngIf="diagnosticInfo.serviceWorker?.scriptUrl">
          <span class="debug-label">Script URL:</span>
          <code>{{ diagnosticInfo.serviceWorker.scriptUrl }}</code>
        </div>
        <div class="debug-row" *ngIf="diagnosticInfo.serviceWorker?.updateFound">
          <span class="debug-label">Update Available:</span>
          <mat-chip color="warn" selected>Yes</mat-chip>
        </div>

        <mat-divider></mat-divider>
        <h4>Subscription Details</h4>
        <div class="debug-row" *ngIf="diagnosticInfo.subscription">
          <span class="debug-label">Endpoint:</span>
          <code class="small-text">{{ diagnosticInfo.subscription.endpoint }}</code>
        </div>
        <div class="debug-row" *ngIf="diagnosticInfo.subscription?.keys">
          <span class="debug-label">Auth Key:</span>
          <code class="small-text">{{ formatJsonProperty(diagnosticInfo.subscription.keys, 'auth') }}</code>
        </div>
        <div class="debug-row" *ngIf="diagnosticInfo.subscription?.keys">
          <span class="debug-label">P256DH Key:</span>
          <code class="small-text">{{ formatJsonProperty(diagnosticInfo.subscription.keys, 'p256dh') }}</code>
        </div>
        <div class="debug-row" *ngIf="!diagnosticInfo.subscription">
          <span class="debug-label">Subscription:</span>
          <mat-chip color="warn" selected>No active subscription</mat-chip>
        </div>
      </div>

      <ng-template #debugError>
        <div class="debug-error">
          <mat-icon>error</mat-icon>
          <span>Failed to load diagnostic information. Click refresh to try again.</span>
        </div>
      </ng-template>
    </mat-expansion-panel>
  </div>

  <!-- Loading Overlay -->
  <div class="loading-overlay" *ngIf="isLoading">
    <mat-spinner diameter="40"></mat-spinner>
  </div>
</div>
